/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useRef, useEffect } from "react";
import { Preferences } from "@capacitor/preferences";
import { NativeBiometric } from "capacitor-native-biometric";
import useHaptics from "./useHaptics";

export const useBiometricAuth = () => {
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [biometricEnabled, setBiometricEnabled] = useState(false);

  const authInProgress = useRef(false);
  const hasCheckedBiometrics = useRef(false);

  const { successActivity, errorFeedback } = useHaptics();

  // 🔹 Load saved biometric preference on mount
  useEffect(() => {
    const checkBiometricPreference = async () => {
      const { value } = await Preferences.get({ key: "biometricEnabled" });
      setBiometricEnabled(value === "true");
    };
    checkBiometricPreference();
  }, []);

  const toggleBiometric = async (enabled: boolean) => {
    setBiometricEnabled(enabled);
    await Preferences.set({
      key: "biometricEnabled",
      value: enabled ? "true" : "false",
    });
  };

  const recordSuccessfulAuth = async () => {
    await Preferences.set({
      key: "lastBiometricAuth",
      value: Date.now().toString(),
    });
    Preferences.set({
      key: "biometricEnabled",
      value: "true",
    });
  };

  const handleAuthError = (error: any) => {
    let errorMsg = "Authentication failed";
    if (error && typeof error === "object" && "code" in error) {
      switch (error.code) {
        case 10:
          errorMsg = "Authentication failed. Please try again.";
          break;
        case 11:
          errorMsg = "Authentication canceled.";
          break;
        case 12:
          errorMsg = "Switched to device passcode.";
          break;
        case 13:
          errorMsg = "Authentication was canceled by the system.";
          break;
        case 14:
          errorMsg =
            "Device passcode is not set, which is required for authentication.";
          break;
        default:
          errorMsg = `Authentication error: ${error.code}`;
      }
    }
    return errorMsg;
  };

  const performBiometricAuth = async (): Promise<boolean> => {
    if (authInProgress.current) return false;
    authInProgress.current = true;
    setLoading(true);
    try {
      const { isAvailable, biometryType } = await NativeBiometric.isAvailable();

      const biometryName =
        biometryType === 2
          ? "Face ID"
          : biometryType === 1
          ? "Touch ID"
          : "Biometric";

      if (!isAvailable) {
        await NativeBiometric.verifyIdentity({
          reason: "Please verify your identity",
          title: "Authentication Required",
          subtitle: "Use your device passcode to continue",
          useFallback: true,
        });
      } else {
        await NativeBiometric.verifyIdentity({
          reason: "Please verify your identity",
          title: "Authentication Required",
          subtitle: `Use ${biometryName} to continue`,
          useFallback: true,
        });
      }

      await recordSuccessfulAuth();
      successActivity();
      hasCheckedBiometrics.current = true;
      return true;
    } catch (error: any) {
      errorFeedback();
      throw handleAuthError(error);
    } finally {
      setLoading(false);
      authInProgress.current = false;
    }
  };

  return {
    performBiometricAuth,
    biometricEnabled,
    toggleBiometric,
    loading,
    loadingMessage,
  };
};
